import { useEffect, useRef, useState } from "react";

interface UseHideOnScrollOptions {
  threshold?: number;
  topZone?: number;
  maxWidth?: number;
  isDisabled?: () => boolean;
}

export const useHideOnScroll = ({
  threshold = 10,
  topZone = 100,
  maxWidth = 800,
  isDisabled,
}: UseHideOnScrollOptions = {}) => {
  const [isHidden, setIsHidden] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const lastScrollY = useRef(0);
  const rafId = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      cancelAnimationFrame(rafId.current);

      rafId.current = requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        setIsScrolled(currentScrollY > topZone);

        if (window.innerWidth > maxWidth) {
          setIsHidden(false);
          lastScrollY.current = currentScrollY;
          return;
        }

        if (isDisabled?.()) {
          setIsHidden(false);
          lastScrollY.current = currentScrollY;
          return;
        }

        if (currentScrollY < topZone) {
          setIsHidden(false);
          lastScrollY.current = currentScrollY;
          return;
        }

        const delta = currentScrollY - lastScrollY.current;

        if (Math.abs(delta) < threshold) return;

        setIsHidden(delta > 0);
        lastScrollY.current = currentScrollY;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafId.current);
    };
  }, [threshold, topZone, maxWidth, isDisabled]);

  return { isHidden, isScrolled };
};
